import { Component, Input, OnChanges, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, ValidationErrors, AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Country, ProcessedCountry, createPhoneNumberValidator, PhoneHelper } from '../../../helpers/phone-helper';

@Component({
  selector: 'app-phone-field',
  templateUrl: './phone-field.component.html',
  styleUrls: ['./phone-field.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TranslateModule, HttpClientModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneFieldComponent),
      multi: true
    }
  ]
})
export class PhoneFieldComponent implements ControlValueAccessor, OnInit, OnChanges, Validator {
  @Input() submitted: boolean = false;
  @Input() role: string = '';
  @Input() required: boolean = false;
  @Input() usePrefix: boolean = false;

  countriesModal: boolean = false;
  selectedCode: string = "+33";
  stringInCode: string = "";

  countries: ProcessedCountry[] = [];

  phoneForm: FormGroup;

  searchTerm: string = '';
  filteredCountries: any[] = [];

  private onChange = (value: any) => { };
  private onTouched = () => { };
  private onValidatorChange: () => void = () => {};

  /** Last value from Angular forms; reapplied once country list is ready. */
  private pendingWriteValue: any = null;

  constructor(private fb: FormBuilder, private http: HttpClient) {
    this.phoneForm = this.fb.group({
      phone: ['', this.getValidators()]
    });

    this.phoneForm.valueChanges.subscribe(value => {
      // Utiliser usePrefix pour controler le formatage
      this.emitValueWithPrefixControl(value.phone || '');
    });
  }

  ngOnInit() {
    this.loadCountries();
  }

  private loadCountries() {
    this.http.get<Country[]>('assets/settings/europsCountries.json').subscribe({
      next: (data) => {
        this.countries = PhoneHelper.processCountriesData(data);
        this.filteredCountries = this.countries;

        // Set France as default
        const france = this.countries.find(c => c.code === 'FR');
        if (france) {
          this.selectedCode = france.dialCode;
        }
        this.applyWriteValue();
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        // Fallback to default countries if loading fails
        this.countries = PhoneHelper.getFallbackCountries();
        this.filteredCountries = this.countries;

        // Set France as default
        const france = this.countries.find(c => c.code === 'FR');
        if (france) {
          this.selectedCode = france.dialCode;
        }
        this.applyWriteValue();
      }
    });
  }

  private getValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(createPhoneNumberValidator(() => this.getCountryCode()));
    return validators;
  }

  private getCountryCode(): string {
    const country = this.countries.find(c => c.dialCode === this.selectedCode);
    return country ? country.code : 'FR';
  }

  openCountriesModal(val: boolean) {
    this.countriesModal = val;
  }

  selectCountry(country: ProcessedCountry) {
    this.selectedCode = country.dialCode;
    this.stringInCode = country.dialCode;
    this.openCountriesModal(false);

    // Update validators when country changes
    const phoneControl = this.phoneForm.get('phone');
    if (phoneControl) {
      phoneControl.setValidators(this.getValidators());
      phoneControl.updateValueAndValidity();
      
      // Emettre la valeur avec le controle du prefixe
      this.emitValueWithPrefixControl(phoneControl.value || '');
    }
  }

  onSearchCountry(event: any) {
    const value = event.target.value.toLowerCase();
    this.searchTerm = value;
    if (value.trim() === '') {
      this.filteredCountries = this.countries;
    } else {
      this.filteredCountries = this.countries.filter(
        c => c.name.toLowerCase().includes(value) || c.dialCode.includes(value)
      );
    }
  }

  clearFilter() {
    this.searchTerm = '';
    this.filteredCountries = this.countries;
  }

  writeValue(value: any): void {
    this.pendingWriteValue = value;
    this.applyWriteValue();
  }

  /**
   * Applies `pendingWriteValue` to the inner control. Clears invalid/garbage prefills.
   * Defers non-empty values until `countries` is loaded so dial code can be resolved.
   */
  private applyWriteValue(): void {
    const value = this.pendingWriteValue;

    if (value == null || value === '') {
      this.phoneForm.patchValue({ phone: '' }, { emitEvent: false });
      return;
    }

    if (!this.countries.length) {
      return;
    }

    if (typeof value === 'object' && value.dialCode) {
      const match = this.countries.find(c => c.dialCode === value.dialCode);
      if (match) {
        this.selectedCode = value.dialCode;
      }
    }

    let phoneValue: string;
    if (typeof value === 'object') {
      phoneValue = value.phone != null ? String(value.phone) : '';
    } else {
      phoneValue = String(value);
    }

    phoneValue = phoneValue.trim();
    if (!phoneValue) {
      this.phoneForm.patchValue({ phone: '' }, { emitEvent: false });
      return;
    }

    if (typeof phoneValue === 'string' && phoneValue.startsWith('+')) {
      const selectedCountry = this.getSelectedCountry();
      if (selectedCountry && phoneValue.startsWith(selectedCountry.dialCode)) {
        phoneValue = phoneValue.substring(selectedCountry.dialCode.length).trim();
      }
    }

    let formattedNumber: string;
    if (this.usePrefix) {
      formattedNumber = this.formatPhoneNumber(phoneValue);
    } else {
      formattedNumber = this.formatLocalNumber(phoneValue);
    }

    if (!this.isDisplayPhoneAcceptable(formattedNumber)) {
      this.phoneForm.patchValue({ phone: '' }, { emitEvent: false });
      return;
    }

    this.phoneForm.patchValue({ phone: formattedNumber }, { emitEvent: false });
  }

  /** Empty is fine; non-empty must parse as valid for the selected country. */
  private isDisplayPhoneAcceptable(display: string): boolean {
    const s = (display || '').trim();
    if (!s) {
      return true;
    }
    return PhoneHelper.isPhoneNumberValid(s, this.getCountryCode());
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.phoneForm.disable();
    } else {
      this.phoneForm.enable();
    }
  }

  // Update validators when role changes
  updateRole(role: string) {
    this.role = role;
    const phoneControl = this.phoneForm.get('phone');
    if (phoneControl) {
      phoneControl.setValidators(this.getValidators());
      phoneControl.updateValueAndValidity();
    }
    this.onValidatorChange();
  }
  // Update validators when required changes
  ngOnChanges() {
    const phoneControl = this.phoneForm.get('phone');
    if (phoneControl) {
      phoneControl.setValidators(this.getValidators());
      phoneControl.updateValueAndValidity();
    }
    this.onValidatorChange();
  }

  get phoneControl() {
    return this.phoneForm.get('phone');
  }

  getSelectedCountry(): ProcessedCountry | undefined {
    return this.countries.find(country => country.dialCode === this.selectedCode);
  }

  // Methode pour formater un numero de telephone (avec prefixe)
  formatPhoneNumber(phoneNumber: string): string {
    return PhoneHelper.formatPhoneNumber(phoneNumber, this.getCountryCode());
  }

  // Methode pour formater un numero local (sans prefixe)
  formatLocalNumber(phoneNumber: string): string {
    // Formater le numero local sans ajouter le prefixe
    const countryCode = this.getCountryCode();
    try {
      // Utiliser la meme logique que PhoneHelper mais avec format NATIONAL
      const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();
      const number = phoneUtil.parse(phoneNumber, countryCode);
      
      if (phoneUtil.isValidNumber(number)) {
        return phoneUtil.format(number, require('google-libphonenumber').PhoneNumberFormat.NATIONAL);
      }
    } catch (error) {
      // Si le parsing echoue, retourner le numero tel quel
    }
    return phoneNumber;
  }

  // Gestion de la saisie avec formatage selon usePrefix
  onPhoneInput(event: any) {
    const input = event.target;
    let phoneNumber = input.value;

    // Formater selon usePrefix
    let formattedNumber;
    if (this.usePrefix) {
      // Si usePrefix est true, formater avec prefixe
      formattedNumber = this.formatPhoneNumber(phoneNumber);
    } else {
      // Si usePrefix est false, formater sans prefixe
      formattedNumber = this.formatLocalNumber(phoneNumber);
    }

    if (formattedNumber !== phoneNumber) {
      input.value = formattedNumber;
      this.phoneForm.patchValue({ phone: formattedNumber });
    }

    // Emettre la valeur avec le controle du prefixe
    this.emitValueWithPrefixControl(formattedNumber);
  }

  // Methode pour v├®rifier si un numero est valide
  isPhoneNumberValid(phoneNumber: string): boolean {
    return PhoneHelper.isPhoneNumberValid(phoneNumber, this.getCountryCode());
  }

  // Methode pour obtenir le type de numero (mobile, fixed-line, etc.)
  getPhoneNumberType(phoneNumber: string): string {
    return PhoneHelper.getPhoneNumberType(phoneNumber, this.getCountryCode());
  }

  // Emettre la valeur avec controle du prefixe selon usePrefix
  private emitValueWithPrefixControl(formattedNumber: string): void {
    let finalPhoneValue;
    
    if (this.usePrefix) {
      // Si usePrefix est true, ajouter le prefixe du pays au numero formate
      finalPhoneValue = this.selectedCode + ' ' + formattedNumber;
    } else {
      // Si usePrefix est false, utiliser le numero formate sans prefixe
      finalPhoneValue = formattedNumber;
    }
    
    const finalValue = {
      phone: finalPhoneValue,
      countryCode: this.getCountryCode(),
      dialCode: this.selectedCode
    };
    
    this.onChange(finalValue);
  }

  // Validator interface implementation: expose inner control errors to parent form
  validate(control: AbstractControl): ValidationErrors | null {
    const phoneControl = this.phoneForm.get('phone');
    if (!phoneControl) {
      return null;
    }
    // Ensure required is enforced at parent level too
    if (this.required && (!phoneControl.value || String(phoneControl.value).trim() === '')) {
      return { required: true };
    }
    return phoneControl.errors || null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}
